import React from 'react';
import { useConsultorioVoiceAssistant } from '../../hooks/useConsultorioVoiceAssistant';

/**
 * Botón flotante de voz en todo el consultorio.
 */
export default function ConsultorioVoiceFab() {
  const {
    open,
    setOpen,
    listening,
    transcribing,
    feedback,
    liveTranscript,
    backendReady,
    provider,
    awaitingConfirm,
    confirmSummary,
    pressTalk,
    releaseTalk,
    confirmPending,
    rejectPending,
    runIntent,
  } = useConsultorioVoiceAssistant();

  const examples = [
    'citas de hoy',
    'agendar cita para el jueves',
    'información del paciente Ana',
    'abrir productos',
    'ver facturas',
    'ayuda',
  ];

  return (
    <div className={`csl-voice-fab-root ${open ? 'is-open' : ''}`}>
      {open ? (
        <div className="csl-voice-panel" role="dialog" aria-label="Asistente de voz">
          <header className="csl-voice-panel__head">
            <div>
              <h3>Asistente de voz</h3>
              <p className="csl-voice-panel__status">
                {listening ? 'Escuchando…' : transcribing ? 'Procesando…' : backendReady
                  ? `Listo · ${provider === 'openai' ? 'OpenAI' : 'Whisper'}`
                  : 'Sin motor'}
              </p>
            </div>
            <button
              type="button"
              className="csl-voice-panel__close"
              aria-label="Cerrar asistente"
              onClick={() => setOpen(false)}
            >
              <i className="bi bi-x-lg" aria-hidden />
            </button>
          </header>

          <p className="csl-voice-panel__hint">
            Mantené el micrófono, hablá y soltá. Sirve para citas, pacientes, productos, facturas e indicaciones.
          </p>

          <div className="csl-voice-panel__mic-wrap">
            <button
              type="button"
              className={[
                'csl-voice-mic',
                listening ? 'is-holding' : '',
                transcribing ? 'is-busy' : '',
              ].filter(Boolean).join(' ')}
              disabled={!backendReady || transcribing}
              onPointerDown={(e) => {
                e.preventDefault();
                try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
                pressTalk();
              }}
              onPointerUp={() => releaseTalk()}
              onPointerCancel={() => releaseTalk()}
              onLostPointerCapture={() => releaseTalk()}
            >
              {transcribing ? (
                <span className="spinner-border spinner-border-sm" role="status" />
              ) : (
                <i className={`bi ${listening ? 'bi-mic-fill' : 'bi-mic'}`} aria-hidden />
              )}
            </button>
            <span className="csl-voice-mic__label">
              {transcribing ? 'Procesando' : listening ? 'Soltá' : 'Mantener'}
            </span>
          </div>

          <div className="csl-voice-chips" aria-label="Ejemplos">
            {examples.map((cmd) => (
              <button
                key={cmd}
                type="button"
                className="csl-voice-chip"
                onClick={() => runIntent(cmd)}
              >
                {cmd}
              </button>
            ))}
          </div>

          {awaitingConfirm ? (
            <div className="csl-voice-confirm">
              <p>
                ¿Está correcto agendar?
                {confirmSummary ? <strong> {confirmSummary}</strong> : null}
              </p>
              <div className="csl-voice-confirm__actions">
                <button type="button" className="cp-btn cp-btn--sm cp-btn--primary" onClick={confirmPending}>
                  Sí, agendar
                </button>
                <button type="button" className="cp-btn cp-btn--sm" onClick={rejectPending}>
                  No
                </button>
              </div>
            </div>
          ) : null}

          <div className="csl-voice-result">
            <span className="csl-voice-result__label">Reconocido</span>
            <div className="csl-voice-result__text">
              {liveTranscript || <span className="text-muted">Todavía no hay audio.</span>}
            </div>
            {feedback ? <div className="csl-voice-result__feedback">{feedback}</div> : null}
          </div>
        </div>
      ) : null}

      <button
        type="button"
        className={[
          'csl-voice-fab',
          open ? 'is-open' : '',
          listening ? 'is-listening' : '',
        ].filter(Boolean).join(' ')}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Cerrar asistente de voz' : 'Abrir asistente de voz'}
        aria-expanded={open}
        title="Asistente de voz"
      >
        <i className={`bi ${open ? 'bi-x-lg' : 'bi-mic-fill'}`} aria-hidden />
        <span className="csl-voice-fab__tip" aria-hidden>Voz</span>
      </button>
    </div>
  );
}
